import { IgAlert, IgTypography } from "@intelligems/ig-theme";
import { Box, Stack } from "@mui/material";

import { formatTemplateConfigJson } from "./templateConfigJson";

/**
 * The disclosure half of the "buffer is a view of the rule's params" contract:
 * the rule's configuration changed outside this workspace — Atlas writes it
 * server-side from the chat rail — while the merchant had unsaved edits.
 *
 * Neither side can be picked for them: adopting would discard their keystrokes,
 * saving would republish over an agent edit they never saw. So Save is held and
 * the two ways out are named here. The incoming configuration is shown rather
 * than described, because "pick a side" is not answerable without it.
 *
 * Pure presentation — the hook owns the buffer and both resolutions.
 */
export const TemplateConfigConflictBanner = ({
  conflictParams,
  onTakeTheirs,
  onKeepMine,
}: {
  conflictParams: Record<string, unknown>;
  onTakeTheirs: () => void;
  onKeepMine: () => void;
}) => (
  <Box sx={{ px: 2, pt: 2, pb: 1 }}>
    <IgAlert
      alertKey="agentic-logic-template-config-conflict"
      severity="warning"
      title="This rule changed while you were editing"
      message={
        <Stack spacing={1}>
          <IgTypography color="text.secondary">
            Atlas saved a different configuration for this rule. Save is paused
            so your edits are not published over it.
          </IgTypography>
          <IgTypography
            component="pre"
            variant="body2"
            color="text.secondary"
            sx={{ m: 0, overflowX: "auto", whiteSpace: "pre" }}
          >
            {formatTemplateConfigJson(conflictParams)}
          </IgTypography>
        </Stack>
      }
      primaryButton={{ cta: "Use Atlas's version", onClick: onTakeTheirs }}
      secondaryButton={{ cta: "Keep my edits", onClick: onKeepMine }}
    />
  </Box>
);
